"use client"
import { FlexCenter, FlexEnd, TextBody, UnstyledButton } from "@opensea/ui-kit"
import { useIsHydrated, useLocalStorage } from "@opensea/ui-kit/hooks"
import { Close } from "@opensea/ui-kit/icons"
import { useTranslations } from "next-intl"
import { useOutageBanner } from "../../hooks/use-flags"

type DismissalData = {
  messageHash: string
  expiresAt: number
}

export function OutageBanner() {
  const t = useTranslations("OutageBanner")
  const { enabled, message } = useOutageBanner()
  const displayMessage = message ?? t("message")

  const [dismissalData, setDismissalData] =
    useLocalStorage<DismissalData | null>("outage-banner-dismissal", null)
  const isHydrated = useIsHydrated()

  // Simple hash to avoid storing actual message content
  const hashMessage = (msg: string) => {
    let hash = 0
    for (let i = 0; i < msg.length; i++) {
      hash = ((hash << 5) - hash + msg.charCodeAt(i)) | 0
    }
    return hash.toString()
  }

  const currentMessageHash = hashMessage(displayMessage)
  const now = Date.now()

  // Check if banner is dismissed for current message and not expired
  const isDismissed =
    dismissalData !== null &&
    dismissalData.messageHash === currentMessageHash &&
    dismissalData.expiresAt > now

  if (!enabled || isDismissed || !isHydrated) {
    return null
  }

  const handleDismiss = () => {
    // Dismiss for 24 hours
    const expiresAt = Date.now() + 24 * 60 * 60 * 1000
    setDismissalData({
      messageHash: currentMessageHash,
      expiresAt,
    })
  }

  return (
    <FlexCenter
      className="relative w-full bg-error-1-transparent-1 px-3 py-4"
      role="banner"
    >
      <FlexCenter className="mx-auto max-w-7xl gap-3">
        <TextBody
          className="text-center font-mono uppercase text-error-1"
          size="xs"
        >
          {displayMessage}
        </TextBody>
      </FlexCenter>
      <FlexEnd>
        <UnstyledButton onClick={handleDismiss}>
          <Close className="text-error-1" size={16} />
        </UnstyledButton>
      </FlexEnd>
    </FlexCenter>
  )
}
